'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Button, Avatar, Dropdown, Drawer } from 'antd';
import {
  SearchOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  AlertOutlined,
  ToolOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  MenuOutlined,
  AppstoreOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { ThemeSelector } from '@/shared/theme';
import { useRouter, usePathname } from 'next/navigation';
import { createPortal } from 'react-dom';
import { useTheme } from '@/shared/theme';
import { useAuth } from '@/domains/auth';
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements';
import { useAppSelector, useAppDispatch } from '@/shared/providers/hooks';
import {
  selectIsMobile,
  toggleMobileSidebar
} from '@/shared/providers/state/navigationSlice';
import {
  DESK_SESSION_LINKS,
  getActiveDeskSessionId,
  type DeskSessionId,
} from './deskSessionNav';
import chrome from './AppChrome.module.css';

/** Rest label — long hint only after expand. */
const SEARCH_PLACEHOLDER = 'Search…';

const DESK_SEARCH_ICONS: Record<DeskSessionId, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  markets: <GlobalOutlined />,
  portfolio: <BarChartOutlined />,
  agents: <ThunderboltOutlined />,
  trading: <ShoppingCartOutlined />,
  activity: <FileTextOutlined />,
  settings: <SettingOutlined />,
  chat: <MessageOutlined />,
  libraries: <AppstoreOutlined />,
};

const Navbar: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  /** Expanded search well on focus / ⌘K — pro header pattern (ui-ux-pro-max density 8) */
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<{ title: string; path: string; tag?: string; icon?: React.ReactNode; description?: string }[]>([]);
  const searchRef = useRef<HTMLInputElement | null>(null);
  /** Slot + wrap: slot is in-flow spacer; wrap is the expanding field */
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [overlayRect, setOverlayRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const { token, isDark, type: currentTheme, utils } = useTheme();
  const { logout } = useAuth();
  const {
    isFree,
    canUseAgents,
    canUseGridDca,
    isLoading: planLoading,
  } = usePlanEntitlements();
  const showAgentUpgrade = !planLoading && isFree && !canUseAgents;
  const showLibrariesUpgrade = !planLoading && !canUseGridDca;
  const dispatch = useAppDispatch();

  // Mobile responsiveness state
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Prevent hydration mismatch by ensuring client-only rendering
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleUserMenuClick = async ({ key }: { key: string }) => {
    switch (key) {
      case 'settings':
        router.push('/settings');
        break;
      case 'logout':
        try {
          await logout();
        } catch (error) {
          console.error('Logout failed:', error);
        }
        break;
    }
  };

  // Get mobile state and sidebar controls from navigation store
  const isMobile = useAppSelector(selectIsMobile);

  // Use default values during SSR to prevent hydration mismatch
  const isMobileSafe = mounted ? isMobile : false;

  // User menu — Plan & billing lives under Settings (profile), not header chrome
  const userMenuItems = useMemo(() => {
    const items: Array<
      | { key: string; label: string; icon?: React.ReactNode; danger?: boolean }
      | { type: 'divider' }
    > = [];

    items.push({
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
    });
    items.push({ type: 'divider' });
    items.push({
      key: 'logout',
      label: 'Sign out',
      icon: <LogoutOutlined />,
      danger: true,
    });

    return items;
  }, []);

  // Fine-tech chrome vars — flat surfaces; CSS module owns hairlines (no sand wells)
  const chromeVars = useMemo(() => {
    const fills = utils.getThemeFills(token, currentTheme);
    const borders = utils.getThemeBorders(token, currentTheme);
    const texts = utils.getThemeTexts(token, currentTheme);
    const shadows = utils.getThemeShadows(token, currentTheme);
    const elevatedShadow = isDark
      ? `${shadows.elevated}, 0 12px 32px rgba(0,0,0,0.4)`
      : 'var(--shadow-lg, 0 8px 24px rgba(20,16,12,0.12), 0 20px 40px rgba(20,16,12,0.1))';

    return {
      fills,
      borders,
      texts,
      shadows,
      elevatedShadow,
      style: {
        background: 'var(--shell-navbar-bg, #FFFEFC)',
        boxShadow: 'none',
        ['--chrome-bg' as string]: 'var(--shell-navbar-bg, #FFFEFC)',
        ['--chrome-well' as string]: 'var(--search-well-bg, var(--shell-navbar-bg, #2A211C))',
        ['--chrome-elevated' as string]:
          'var(--color-bg-elevated, var(--color-card-elevated, #FFFFFF))',
        ['--chrome-border' as string]:
          'var(--shell-navbar-border, #D4C0AE)',
        ['--chrome-primary' as string]: 'var(--color-primary, ' + token.colorPrimary + ')',
        ['--chrome-primary-subtle' as string]: fills.primarySubtle,
        ['--chrome-hover-bg' as string]: fills.hoverBg,
        ['--chrome-focus-border' as string]:
          'var(--color-input-border-focus, var(--color-primary, ' + token.colorPrimary + '))',
        ['--chrome-focus-shadow' as string]: shadows.focus,
        ['--chrome-elevated-shadow' as string]: elevatedShadow,
        ['--chrome-text' as string]: 'var(--color-text-primary, ' + (texts.primary || '#14100C') + ')',
        ['--chrome-text-muted' as string]:
          'var(--color-text-secondary, ' + (texts.secondary || texts.tertiary || '#3A2A22') + ')',
        ['--chrome-text-accent' as string]:
          'var(--color-primary, ' + (texts.accent || token.colorPrimary) + ')',
        ['--chrome-on-primary' as string]: 'var(--color-on-primary, #FFFCFA)',
      } as React.CSSProperties,
    };
  }, [token, isDark, currentTheme, utils]);

  const dataIndex = useMemo(() => {
    const agentNote = showAgentUpgrade
      ? 'Operator plan required — open Settings to manage plan.'
      : undefined;
    const librariesNote = showLibrariesUpgrade
      ? 'Desk plan required — multi-exchange grid/DCA libraries.'
      : undefined;

    // Desk surfaces for search only — primary nav lives in the sidebar (no header duplicate)
    const deskEntries = DESK_SESSION_LINKS.map((link) => {
      const needsAgentUpgrade =
        showAgentUpgrade && (link.id === 'agents' || link.id === 'trading');
      const needsLibrariesUpgrade =
        showLibrariesUpgrade && link.id === 'libraries';
      const needsUpgrade = needsAgentUpgrade || needsLibrariesUpgrade;
      const icon = DESK_SEARCH_ICONS[link.id];
      let description = link.description;
      if (needsAgentUpgrade && link.id === 'trading') {
        description = `Order execution · ${agentNote}`;
      } else if (needsAgentUpgrade && link.id === 'agents') {
        description = `Run and supervise agents · ${agentNote}`;
      } else if (needsLibrariesUpgrade) {
        description = `Grid & DCA libraries · ${librariesNote}`;
      }
      return {
        title: link.label,
        path: link.href,
        tag: needsUpgrade ? 'upgrade' : link.tag,
        icon,
        description,
      };
    });

    return [
      ...deskEntries,
      {
        title: 'Execution',
        path: '/trading/execution',
        tag: showAgentUpgrade ? 'upgrade' : 'execution',
        icon: <ShoppingCartOutlined />,
        description: showAgentUpgrade
          ? `Order execution · ${agentNote}`
          : 'Order execution and monitoring on the desk',
      },
      { title: 'Dashboard', path: '/dashboard', tag: 'page', icon: <DashboardOutlined />, description: 'Main trading dashboard' },
      { title: 'Client dashboard', path: '/client/dashboard', tag: 'page', icon: <DashboardOutlined />, description: 'Client desk overview' },
      { title: 'Analytics', path: '/analytics', tag: 'insights', icon: <BarChartOutlined />, description: 'Advanced desk analytics' },
      { title: 'Help', path: '/help', tag: 'support', icon: <QuestionCircleOutlined />, description: 'Help and operator guidance' },
      { title: 'Reports', path: '/reports', tag: 'insights', icon: <FileTextOutlined />, description: 'Trading reports & insights' },
      { title: 'Alerts', path: '/alerts', tag: 'monitoring', icon: <AlertOutlined />, description: 'System alerts & notifications' },
      { title: 'System Logs', path: '/system/logs', tag: 'admin', icon: <ToolOutlined />, description: 'System administration logs' },
      {
        title: 'Algorithmic Trading',
        path: '/algo-trading',
        tag: showAgentUpgrade ? 'upgrade' : 'algo',
        icon: <ThunderboltOutlined />,
        description: showAgentUpgrade
          ? `Strategies · ${agentNote}`
          : 'Algorithmic trading strategies',
      },
      ...(showAgentUpgrade || showLibrariesUpgrade
        ? [
          {
            title: 'Plan & billing',
            path: '/settings',
            tag: 'settings',
            icon: <SettingOutlined />,
            description: 'Change plan from profile Settings — not the header',
          },
        ]
        : []),
    ];
  }, [showAgentUpgrade, showLibrariesUpgrade]);

  // Keyboard navigation — active while search owns focus (expanded or dropdown open)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open && !searchExpanded) return;

      switch (e.key) {
        case 'ArrowDown':
          if (!open || results.length === 0) return;
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          if (!open || results.length === 0) return;
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          if (results.length > 0 && selectedIndex >= 0 && selectedIndex < results.length) {
            e.preventDefault();
            const selectedResult = results[selectedIndex];
            if (selectedResult) {
              selectResult(selectedResult.path);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (searchValue.trim()) {
            // First Esc clears query; second collapses (pro command-palette pattern)
            setSearchValue('');
            setResults([]);
            setSelectedIndex(0);
          } else {
            setOpen(false);
            setSearchExpanded(false);
            setSelectedIndex(0);
            searchRef.current?.blur();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, searchExpanded, results, selectedIndex, searchValue, router]);

  // Global keyboard shortcut (Cmd/Ctrl + K) — expand + focus search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchExpanded(true);
        setOpen(true);
        // Defer focus until expand layout paints (smoother measure for dropdown)
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      // Expanded idle: first desk surfaces as quick jumps (command-palette pattern)
      if (searchExpanded || open) {
        // dataIndex leads with DESK_SESSION_LINKS — take full desk set
        setResults(dataIndex.slice(0, DESK_SESSION_LINKS.length));
      } else {
        setResults([]);
      }
      setSelectedIndex(0);
      return;
    }
    const q = searchValue.toLowerCase();
    const filtered = dataIndex.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.tag?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    );
    setResults(filtered.slice(0, 8));
    setSelectedIndex(0);
  }, [searchValue, dataIndex, searchExpanded, open]);

  useEffect(() => {
    const handleDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideSlot = containerRef.current?.contains(target) ?? false;
      const clickedInsideWrap = searchWrapRef.current?.contains(target) ?? false;
      const clickedInsideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!clickedInsideSlot && !clickedInsideWrap && !clickedInsideDropdown) {
        setOpen(false);
        setSearchExpanded(false);
        setSelectedIndex(0);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  useEffect(() => {
    if (!open && !searchExpanded) return;
    const update = () => {
      // Measure the expanding wrap (not the 156px spacer) so dropdown matches field
      const el = searchWrapRef.current ?? containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setOverlayRect({
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, searchExpanded ? 360 : rect.width),
      });
    };
    // Double rAF + mid-transition tick so width animation tracks dropdown
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });
    const mid = window.setTimeout(update, 130);
    const end = window.setTimeout(update, 260);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(id);
      window.clearTimeout(mid);
      window.clearTimeout(end);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, searchExpanded, searchValue]);

  const selectResult = (path: string) => {
    router.push(path);
    setOpen(false);
    setSearchExpanded(false);
    setMobileSearchOpen(false);
    setSearchValue('');
    setSelectedIndex(0);
  };

  const expandSearch = () => {
    setSearchExpanded(true);
    setOpen(true);
  };

  /** Collapse only when empty — keep open while a query is active (pro palette UX). */
  const collapseSearchIfIdle = () => {
    if (!searchValue.trim()) {
      setSearchExpanded(false);
      setOpen(false);
    }
  };

  const focusSearchInput = () => {
    expandSearch();
    // Defer so width transition has a frame to start before caret lands
    requestAnimationFrame(() => {
      searchRef.current?.focus();
    });
  };

  const { fills, borders, texts } = chromeVars;

  const activeDeskId = getActiveDeskSessionId(pathname);
  const activeDeskLabel =
    DESK_SESSION_LINKS.find((link) => link.id === activeDeskId)?.label ?? 'Desk';

  return (
    <>
      <header
        suppressHydrationWarning
        className={[
          chrome.navbar,
          isMobileSafe ? chrome.navbarMobile : '',
          open || mobileSearchOpen ? chrome.navbarRaised : '',
          searchExpanded && !isMobileSafe ? chrome.navbarSearchExpanded : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={chromeVars.style}
        role="banner"
      >
        {/*
          Left — global tools only (menu on mobile / expand-on-focus search).
          Primary desk routes live in the sidebar — not repeated here.
        */}
        <div
          className={[
            chrome.navLeft,
            searchExpanded && !isMobileSafe ? chrome.navLeftSearchExpanded : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {isMobileSafe && (
            <div className={chrome.actions}>
              <Button
                type="text"
                icon={<MenuOutlined />}
                aria-label="Open navigation"
                onClick={() => dispatch(toggleMobileSidebar())}
                className={chrome.iconBtn}
              />
              <Button
                type="text"
                icon={<SearchOutlined />}
                aria-label="Open search"
                onClick={() => setMobileSearchOpen(true)}
                className={chrome.iconBtn}
              />
            </div>
          )}

          {!isMobileSafe && (
            <div
              ref={containerRef}
              className={[
                chrome.searchSlot,
                searchExpanded ? chrome.searchSlotExpanded : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {/*
              Click/focus expands search into open chrome (sidebar owns destinations).
              Spacer (.searchSlot) holds rest width so the bar does not jump.
            */}
              <div
                ref={searchWrapRef}
                className={[
                  chrome.searchWrap,
                  searchExpanded ? chrome.searchWrapExpanded : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={(e) => {
                  // Expand on click even before focus settles (pro toolbar pattern)
                  if (!searchExpanded) {
                    e.preventDefault();
                    focusSearchInput();
                  }
                }}
                role="search"
              >
                <div className={chrome.searchField}>
                  <Input
                    ref={searchRef as any}
                    placeholder={
                      pathname === '/trading'
                        ? 'Search instruments, agents, books...'
                        : searchExpanded
                          ? 'Search markets, portfolio, trading, agents…'
                          : SEARCH_PLACEHOLDER
                    }
                    role="combobox"
                    aria-label="Search desk surfaces"
                    aria-expanded={open}
                    aria-controls={open ? 'desk-search-results' : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={
                      open && results[selectedIndex]
                        ? `desk-search-option-${selectedIndex}`
                        : undefined
                    }
                    prefix={
                      <SearchOutlined
                        aria-hidden
                        style={{
                          color: searchExpanded ? token.colorPrimary : texts.tertiary,
                          fontSize: 14,
                        }}
                      />
                    }
                    suffix={
                      mounted ? (
                        searchExpanded ? (
                          searchValue ? (
                            <button
                              type="button"
                              className={chrome.searchClear}
                              aria-label="Clear search"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setSearchValue('');
                                setResults([]);
                                searchRef.current?.focus();
                              }}
                            >
                              Esc
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={chrome.searchClear}
                              aria-label="Close search"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setOpen(false);
                                setSearchExpanded(false);
                                searchRef.current?.blur();
                              }}
                            >
                              Esc
                            </button>
                          )
                        ) : (
                          <span className={chrome.searchShortcut} aria-hidden="true">
                            <span className={chrome.searchShortcutKey}>⌘</span>
                            <span className={chrome.searchShortcutKey}>K</span>
                          </span>
                        )
                      ) : null
                    }
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (!searchExpanded) expandSearch();
                    }}
                    onPressEnter={() => {
                      if (results.length > 0 && selectedIndex >= 0 && selectedIndex < results.length) {
                        const selectedResult = results[selectedIndex];
                        if (selectedResult) {
                          selectResult(selectedResult.path);
                        }
                      } else {
                        setOpen(false);
                      }
                    }}
                    className={[
                      chrome.searchInput,
                      searchExpanded ? chrome.searchInputExpanded : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onFocus={expandSearch}
                    onBlur={(e) => {
                      // Don't collapse when focus moves to clear/Esc control or dropdown
                      const next = e.relatedTarget as Node | null;
                      if (
                        next &&
                        (containerRef.current?.contains(next) ||
                          dropdownRef.current?.contains(next))
                      ) {
                        return;
                      }
                      // Brief delay so mousedown on result can fire first
                      window.setTimeout(() => {
                        if (
                          dropdownRef.current?.contains(document.activeElement) ||
                          containerRef.current?.contains(document.activeElement)
                        ) {
                          return;
                        }
                        collapseSearchIfIdle();
                      }, 120);
                    }}
                  />
                </div>
                {open && overlayRect && mounted &&
                  createPortal(
                    <div
                      ref={dropdownRef}
                      id="desk-search-results"
                      className={chrome.searchDropdown}
                      role="listbox"
                      aria-label="Search results"
                      style={{
                        top: overlayRect.top,
                        left: overlayRect.left,
                        width: Math.max(overlayRect.width, 320),
                        ['--chrome-bg' as string]:
                          'var(--color-bg-container, var(--color-card, #FFFCFA))',
                        ['--chrome-elevated' as string]:
                          'var(--color-bg-elevated, var(--color-card-elevated, #FFFFFF))',
                        ['--chrome-border' as string]:
                          'var(--color-border, ' + (borders.default || '#B89F84') + ')',
                        ['--chrome-primary' as string]:
                          'var(--color-primary, ' + token.colorPrimary + ')',
                        ['--chrome-hover-bg' as string]: fills.hoverBg,
                        ['--chrome-elevated-shadow' as string]: chromeVars.elevatedShadow,
                        ['--chrome-well' as string]:
                          'var(--color-surface-secondary, #EDE3D6)',
                      }}
                    >
                      <div className={chrome.searchDropdownHeader}>
                        <span className={chrome.searchDropdownTitle}>
                          <SearchOutlined aria-hidden />
                          {searchValue.trim()
                            ? results.length > 0
                              ? 'Results'
                              : 'No matches'
                            : 'Desk surfaces'}
                        </span>
                        <span className={chrome.searchResultCount}>
                          {searchValue.trim()
                            ? `${results.length}`
                            : '⌘K'}
                        </span>
                      </div>

                      {results.length > 0 ? (
                        <div className={chrome.searchResultsList}>
                          <div>
                          {results.map((item, index) => {
                              const selected = index === selectedIndex;
                              return (
                                <div
                                  key={item.path}
                                  id={`desk-search-option-${index}`}
                                  role="option"
                                  aria-selected={selected}
                                  className={[
                                    chrome.searchResult,
                                    selected ? chrome.searchResultSelected : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onMouseEnter={() => setSelectedIndex(index)}
                                  onClick={() => selectResult(item.path)}
                                >
                                  <div className={chrome.searchResultInner}>
                                    <span className={chrome.searchResultIcon} aria-hidden>
                                      {item.icon}
                                    </span>
                                    <span className={chrome.searchResultCopy}>
                                      <span className={chrome.searchResultTitle}>
                                        {item.title}
                                      </span>
                                      {item.description ? (
                                        <span className={chrome.searchResultDescription}>
                                          {item.description}
                                        </span>
                                      ) : null}
                                    </span>
                                    {item.tag ? (
                                      <span className={chrome.searchResultTag}>{item.tag}</span>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        </div>
                      ) : null}

                      {results.length === 0 && searchValue.trim().length > 0 ? (
                        <div className={chrome.searchEmpty} role="status">
                          <SearchOutlined aria-hidden />
                          <p className={chrome.searchEmptyTitle}>
                            No results for “{searchValue}”
                          </p>
                          <p className={chrome.searchEmptyHint}>
                            Try dashboard, markets, trade, agents, or settings
                          </p>
                        </div>
                      ) : null}
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          )}

          {!isMobileSafe && (
            <p
              className={chrome.location}
              data-desk-location=""
              aria-current="page"
            >
              {activeDeskLabel}
            </p>
          )}
        </div>

        {/* Right — theme, alerts, account, then chat as the last control */}
        <div className={chrome.navRight}>
          <div className={chrome.actions}>
            <ThemeSelector variant="trigger" showMode />
            {!isMobileSafe && (
              <Button
                type="text"
                icon={<BellOutlined />}
                aria-label="Notifications"
                className={chrome.iconBtn}
                onClick={() => router.push('/notifications')}
              />
            )}
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <button
                type="button"
                className={chrome.avatarBtn}
                aria-label="Account menu"
                aria-haspopup="menu"
              >
                <Avatar
                  size={isMobileSafe ? 28 : 32}
                  shape="circle"
                  icon={<UserOutlined />}
                  className={chrome.accountAvatar}
                />
              </button>
            </Dropdown>
            <Button
              type="text"
              icon={<MessageOutlined />}
              aria-label="Open desk chat"
              aria-current={activeDeskId === 'chat' ? 'page' : undefined}
              className={[
                chrome.iconBtn,
                activeDeskId === 'chat' ? chrome.iconBtnCurrent : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => router.push('/chat-ai')}
            />
          </div>
        </div>
      </header>

      {/* Mobile Search Drawer — same instrument list language as desktop pop-out */}
      <Drawer
        title="Search"
        placement="top"
        size="100%"
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        closable
        styles={{
          body: {
            padding: 12,
            background: 'var(--color-card, #fffcfa)',
          },
          header: {
            background: 'var(--color-card, #fffcfa)',
            borderBottom: '1px solid var(--color-border, #d4c0ae)',
          },
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder={SEARCH_PLACEHOLDER}
            aria-label="Search desk surfaces"
            prefix={<SearchOutlined aria-hidden style={{ color: token.colorPrimary }} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size="large"
            autoFocus
            className={chrome.searchInput}
            style={{ minHeight: 44 }}
          />
        </div>

        {results.length > 0 ? (
          <div>
            {results.map((item, index) => (
              <div
                key={item.path}
                id={`desk-search-mobile-option-${index}`}
                role="option"
                className={chrome.searchResult}
                onClick={() => selectResult(item.path)}
              >
                <div className={chrome.searchResultInner}>
                  <span className={chrome.searchResultIcon} aria-hidden>
                    {item.icon}
                  </span>
                  <span className={chrome.searchResultCopy}>
                    <span className={chrome.searchResultTitle}>{item.title}</span>
                    {item.description ? (
                      <span className={chrome.searchResultDescription}>
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                  {item.tag ? (
                    <span className={chrome.searchResultTag}>{item.tag}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {results.length === 0 && searchValue.trim() ? (
          <div className={chrome.searchEmpty} role="status">
            <p className={chrome.searchEmptyTitle}>
              No results for “{searchValue}”
            </p>
            <p className={chrome.searchEmptyHint}>
              Try dashboard, markets, trade, agents, or settings
            </p>
          </div>
        ) : null}
      </Drawer>
    </>
  );
};

export default Navbar;
